/**
 * AsterPanel — live perpetuals trading on Aster DEX. Scaffold: the order
 * ticket, positions and orders layout are in place, but submitting is off
 * until the server reports the Aster API wallet, a trade token and order
 * signing are all in place (GET /api/exchange/aster/status).
 */

import { memo, useEffect, useState } from 'react';
import { useTerminalStore } from '../../stores/terminal.store.js';
import { formatPriceWithSymbol } from '@arc/shared';

type Status = {
  configured: boolean; missing: string[]; tradeTokenSet: boolean; ordersEnabled: boolean; reason: string;
};

const inputStyle: React.CSSProperties = {
  background: 'rgba(255,59,59,0.05)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-bright)',
};

function useAsterStatus(): Status | null | 'error' {
  const [status, setStatus] = useState<Status | null | 'error'>(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => fetch('/api/exchange/aster/status')
      .then(r => (r.ok ? r.json() as Promise<Status> : Promise.reject(new Error(String(r.status)))))
      .then(s => { if (!cancelled) setStatus(s); }, () => { if (!cancelled) setStatus('error'); });
    void load();
    const t = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  return status;
}

export const AsterPanel = memo(function AsterPanel() {
  const status  = useAsterStatus();
  const tokens  = useTerminalStore(s => s.tokens);
  const prices  = useTerminalStore(s => s.prices);
  const [symbol, setSymbol]     = useState('BTC');
  const [side, setSide]         = useState<'BUY' | 'SELL'>('BUY');
  const [type, setType]         = useState<'MARKET' | 'LIMIT'>('MARKET');
  const [sizeUsd, setSizeUsd]   = useState('100');
  const [leverage, setLeverage] = useState('5');
  const [limit, setLimit]       = useState('');
  const [reduceOnly, setReduceOnly] = useState(false);

  const ready  = status !== null && status !== 'error' && status.ordersEnabled;
  const reason = status === null ? 'Checking Aster connection…'
    : status === 'error' ? 'API unreachable'
    : status.reason;
  const mark   = prices[symbol]?.priceUsd;

  return (
    <div className="h-full flex flex-col font-mono text-[11px] overflow-y-auto">
      {/* Connection banner */}
      <div
        role="status"
        className="px-3 py-2 border-b"
        style={{ borderColor: 'var(--color-border)', background: ready ? 'rgba(0,255,136,0.06)' : 'rgba(255,59,59,0.08)' }}
      >
        <div className="font-bold tracking-wider" style={{ color: ready ? 'var(--color-buy)' : 'var(--color-sell)' }}>
          {ready ? '● ASTER CONNECTED · LIVE ORDERS ON' : '○ ASTER NOT CONNECTED · ORDERS OFF'}
        </div>
        {status !== null && status !== 'error' && !status.configured ? (
          <div className="text-muted mt-0.5">
            Server setup needed: Aster API wallet ({status.missing.join(', ')}) and ARC_TRADE_TOKEN. Keys stay on the server, never in the browser.
          </div>
        ) : !ready && <div className="text-muted mt-0.5">{reason}</div>}
      </div>

      {/* Order ticket */}
      <div className="px-3 py-2 flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <select aria-label="Aster market" value={symbol} onChange={e => setSymbol(e.target.value)} className="flex-1 px-1 py-0.5" style={inputStyle}>
            {tokens.map(t => <option key={t.symbol} value={t.symbol} className="bg-[#040d1a]">{t.symbol}-USDT PERP</option>)}
          </select>
          <span className="text-muted whitespace-nowrap">mark {mark ? formatPriceWithSymbol(mark) : '—'}</span>
        </div>

        <div className="flex gap-1" role="group" aria-label="Side">
          {(['BUY', 'SELL'] as const).map(s => (
            <button key={s} onClick={() => setSide(s)} aria-pressed={side === s}
              className="flex-1 py-1 font-bold tracking-wider border"
              style={side === s
                ? { background: s === 'BUY' ? 'rgba(0,255,136,0.15)' : 'rgba(255,59,59,0.15)', color: s === 'BUY' ? 'var(--color-buy)' : 'var(--color-sell)', borderColor: 'currentColor' }
                : { color: 'var(--color-bright)', borderColor: 'var(--color-border)' }}
            >{s === 'BUY' ? '▲ LONG' : '▼ SHORT'}</button>
          ))}
        </div>

        <div className="flex gap-1" role="group" aria-label="Order type">
          {(['MARKET', 'LIMIT'] as const).map(t => (
            <button key={t} onClick={() => setType(t)} aria-pressed={type === t}
              className={`flex-1 py-0.5 border tracking-wider ${type === t ? 'text-arc border-arc' : 'text-bright border-hud'}`}
            >{t}</button>
          ))}
        </div>

        <label className="flex items-center gap-2">
          <span className="min-w-[72px] text-muted">SIZE (USD)</span>
          <input type="number" min={1} value={sizeUsd} onChange={e => setSizeUsd(e.target.value)} className="flex-1 min-w-0 px-1 py-0.5" style={inputStyle} />
        </label>
        <label className="flex items-center gap-2">
          <span className="min-w-[72px] text-muted">LEVERAGE</span>
          <input type="number" min={1} max={50} value={leverage} onChange={e => setLeverage(e.target.value)} className="flex-1 min-w-0 px-1 py-0.5" style={inputStyle} />
        </label>
        {type === 'LIMIT' && (
          <label className="flex items-center gap-2">
            <span className="min-w-[72px] text-muted">PRICE</span>
            <input type="number" min={0} value={limit} onChange={e => setLimit(e.target.value)} className="flex-1 min-w-0 px-1 py-0.5" style={inputStyle} />
          </label>
        )}
        <label className="flex items-center gap-2 text-muted">
          <input type="checkbox" checked={reduceOnly} onChange={e => setReduceOnly(e.target.checked)} /> REDUCE ONLY
        </label>

        <button
          disabled={!ready}
          title={ready ? undefined : reason}
          className="mt-1 py-1.5 font-bold tracking-widest border disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ color: side === 'BUY' ? 'var(--color-buy)' : 'var(--color-sell)', borderColor: 'currentColor' }}
        >
          {side === 'BUY' ? 'LONG' : 'SHORT'} {symbol} · LIVE
        </button>
      </div>

      {/* Positions / orders */}
      {(['POSITIONS', 'OPEN ORDERS'] as const).map(title => (
        <div key={title} className="px-3 py-2 border-t" style={{ borderColor: 'var(--color-border)' }}>
          <div className="text-muted tracking-wider mb-1">{title}</div>
          <div className="text-muted opacity-80">{ready ? 'None' : 'Connect Aster to see live data.'}</div>
        </div>
      ))}
    </div>
  );
});
